export const MAXIMUM_FILE_COUNT = 50;
export const MAXIMUM_FILE_SIZE_BYTES = 10_000_000;
export const MAXIMUM_TOTAL_SIZE_BYTES = 30_000_000;

export const ALLOWED_FILE_EXTENSIONS = [
   "png",
   "jpg",
   "jpeg",
   "bmp",
   "pdf",
   "heic",
   "heif",
   "tiff",
   "tif",
] as const;

export const TOO_MANY_FILES_MESSAGE =
   "Najednou lze nahrát nejvýše 50 souborů.";
export const TOTAL_SIZE_EXCEEDED_MESSAGE =
   "Celková velikost nahrávaných souborů překračuje maximální povolenou velikost 30 MB.";
export const GENERIC_UPLOAD_ERROR_MESSAGE =
   "Nahrání souborů se nezdařilo. Zkuste to prosím znovu. Pokud problém přetrvává, kontaktujte podporu.";

const allowedFileExtensions = new Set<string>(ALLOWED_FILE_EXTENSIONS);
const knownErrorCodes = new Set([
   "unsupported_file_format",
   "file_too_large",
   "total_upload_too_large",
   "too_many_files",
   "upload_failed",
]);

interface UploadApiError {
   response: {
      data: {
         errorCode: string;
         errorMessage: string;
      };
   };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
   typeof value === "object" && value !== null;

const isUploadApiError = (error: unknown): error is UploadApiError => {
   if (!isRecord(error) || !isRecord(error.response)) {
      return false;
   }

   const { data } = error.response;
   return (
      isRecord(data) &&
      typeof data.errorCode === "string" &&
      typeof data.errorMessage === "string"
   );
};

export const validateUploadFiles = (files: File[]): string | null => {
   if (files.length > MAXIMUM_FILE_COUNT) {
      return TOO_MANY_FILES_MESSAGE;
   }

   const unsupportedFile = files.find((file) => {
      const extension = file.name.split(".").pop()?.toLowerCase();
      return !extension || !allowedFileExtensions.has(extension);
   });

   if (unsupportedFile) {
      return `Typ souboru ${unsupportedFile.name} není podporován. Povolené přípony jsou .png, .jpg, .jpeg, .bmp, .pdf, .heic, .heif, .tiff a .tif.`;
   }

   const oversizedFile = files.find(
      (file) => file.size > MAXIMUM_FILE_SIZE_BYTES
   );

   if (oversizedFile) {
      return `Soubor ${oversizedFile.name} překračuje maximální povolenou velikost 10 MB.`;
   }

   const totalSize = files.reduce((total, file) => total + file.size, 0);
   if (totalSize > MAXIMUM_TOTAL_SIZE_BYTES) {
      return TOTAL_SIZE_EXCEEDED_MESSAGE;
   }

   return null;
};

export const getUploadErrorMessage = (error: unknown): string => {
   if (
      isUploadApiError(error) &&
      knownErrorCodes.has(error.response.data.errorCode)
   ) {
      return error.response.data.errorMessage;
   }

   return GENERIC_UPLOAD_ERROR_MESSAGE;
};
